import { useCallback, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom-v5-compat";
import { ACTIVITIES_HTS_EVENT_TRANSFORMER_HOOKS } from "@b2bportal/core-activities";
import { AIR_PRICE_WATCH_HTS_EVENT_TRANSFORMER_HOOKS } from "@b2bportal/core-air-price-watch";
import { HOTEL_CHECKOUT_HTS_EVENT_TRANSFORMER_HOOKS } from "@b2bportal/core-checkout";
import { CORE_FLIGHTS_PRICE_FREEZE_HTS_EVENT_TRANSFORMER_HOOKS } from "@b2bportal/core-flights-price-freeze";
import { SELF_SERVE_HTS_EVENT_TRANSFORMER_HOOKS } from "@b2bportal/core-self-serve";
import { CORE_COMMON_HTS_EVENT_TRANSFORMER_HOOKS } from "@b2bportal/core-tracking";
import {
  createUseEmitRawEvent,
  mergeEventTransformerHooks,
  TrackingProvider,
  usePublishHTSEvent,
} from "@b2bportal/core-tracking";
import type { EventTransformerHooks } from "@b2bportal/core-tracking";
import { Funnel } from "@b2bportal/core-types";
import type { RbcPartnerMetadata } from "@b2bportal/rbc-accounts-api";
import { useExperimentTracking } from "@tenant/experiments";
import {
  AppTrackingContext,
  useAppTrackingProvider,
} from "./AppTrackingContext";
import {
  type CheckoutTrackingPublishEvent,
  useCheckoutTrackingSink,
} from "./useCheckoutTrackingSink";
import { UserContext } from "@/App";
import { usePublishRBCEvent } from "@/hooks/shared/usePublishRBCEvent";
import { CARS_CHECKOUT_TRANSFORMER_HOOKS } from "@/modules/cars-checkout/tracking";
import type {
  InsuranceRawEvent,
  InsuranceEvent,
} from "@/modules/flight-checkout/tracking";
import {
  useViewedInsuranceTransformer,
  useViewedInsuranceTermsTransformer,
  useSelectedInsuranceTransformer,
  useSelectTermsTransformer,
  useViewedTermsTransformer,
  InsuranceRawEventName,
  PaymentRawEventName as FlightPaymentRawEventName,
  type PaymentRawEvent as FlightPaymentRawEvent,
  type PaymentEvent as FlightPaymentEvent,
} from "@/modules/flight-checkout/tracking";
import { useGridV2TrackingProperties } from "@/modules/grid-v2/useGridV2TrackingProperties";
import { ALL_RBC_FLIGHT_TRANSFORMERS } from "@/store/flights/tracking";
import {
  ALL_RBC_CARS_TRANSFORMERS,
  ALL_RBC_HOTEL_TRANSFORMERS,
  ALL_RBC_PACKAGE_TRANSFORMERS,
} from "@/tracking";
import {
  useActivityEntryTransformer,
  useCommonAuthTransformer,
  useViewedFlightCheckoutTransformer,
  useViewedCarCheckoutTransformer,
  useViewedHotelCheckoutTransformer,
  useViewedPackageCheckoutTransformer,
  useViewedActivitiesCheckoutTransformer,
  useViewPaymentTransformer,
  useChoosePaymentTransformer,
  useChooseTravelerTransformer,
  useGridV2OptedOutTransformer,
  OFFER_EVENT_TRANSFORMER_HOOKS,
  UP_SELL_EVENT_TRANSFORMER_HOOKS,
  RbcActivityEntryRawEventName,
  RbcAuthRawEventName,
  RbcCheckoutRawEventName,
  PaymentRawEventName,
  TravelerRawEventName,
  GridV2RawEventName,
} from "@/tracking";
import type { RbcRawEvent, RbcEvent } from "@/tracking/types";
import { useGetCurrentFunnel } from "@/utilities/hooks/useGetCurrentFunnel";
import { shouldPushToDataLayer } from "@/utils/shouldPushToDataLayer";

/**
 * Maps Funnel enum to lob (line of business) string value
 */
const funnelToLob = (funnel: Funnel | undefined): string | undefined => {
  if (!funnel) return undefined;

  switch (funnel) {
    case Funnel.Air:
      return "flights";
    case Funnel.Lodging:
      return "hotel";
    case Funnel.Ground:
      return "cars";
    case Funnel.Package:
      return "package";
    case Funnel.Activities:
      return "activity";
    case Funnel.Trips:
      return "trips";
    default:
      return undefined;
  }
};

const usePublishEvent = () => {
  const { sessionInfo } = useContext(UserContext);
  const isSignedIn = sessionInfo?.userScope?.isSignedIn ?? false;
  const experimentTracking = useExperimentTracking(isSignedIn);
  const publish = usePublishHTSEvent();
  const pushToDataLayer = usePublishRBCEvent();
  const location = useLocation();
  // Grid V2 Tracking Spec attribution model: grid_version, grid_v2_opted_out.
  const gridV2Tracking = useGridV2TrackingProperties();

  // Get current funnel and map to lob
  const currentFunnel = useGetCurrentFunnel();
  const lob = funnelToLob(currentFunnel);
  const path = location.pathname;

  // Extract user properties from RBC partner metadata
  const partnerMetadata = sessionInfo?.userInfo?.partnerMetadata as
    | RbcPartnerMetadata
    | undefined;
  const userTier = partnerMetadata?.tierEn?.toLowerCase();
  const userProvince = partnerMetadata?.provinceCode;
  const accountName = partnerMetadata?.accountNameEn;

  return useCallback<CheckoutTrackingPublishEvent>(
    (event) => {
      const enrichedProperties = {
        ...experimentTracking,
        ...gridV2Tracking,
        ...event.properties,
        ...(userTier ? { user_tier: userTier } : {}),
        ...(userProvince ? { user_province: userProvince } : {}),
        ...(accountName ? { accountName } : {}),
        ...(lob ? { lob } : {}),
        ...(path ? { path } : {}),
      };

      publish({
        ...event,
        properties: enrichedProperties,
      });
      // HTS gets everything; GTM never gets a failed complete_buy_* (see shouldPushToDataLayer).
      if (shouldPushToDataLayer(event.name, enrichedProperties)) {
        pushToDataLayer(event.name, enrichedProperties);
      }
    },
    [
      publish,
      pushToDataLayer,
      experimentTracking,
      gridV2Tracking,
      userTier,
      userProvince,
      accountName,
      lob,
      path,
    ]
  );
};

const RBC_SPECIFIC_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  RbcRawEvent,
  RbcEvent
> = {
  [RbcActivityEntryRawEventName.ActivityEntry]: [useActivityEntryTransformer],
  [RbcAuthRawEventName.CommonAuth]: [useCommonAuthTransformer],
  [RbcCheckoutRawEventName.ViewedFlightCheckout]: [
    useViewedFlightCheckoutTransformer,
  ],
  [RbcCheckoutRawEventName.ViewedCarCheckout]: [
    useViewedCarCheckoutTransformer,
  ],
  [RbcCheckoutRawEventName.ViewedHotelCheckout]: [
    useViewedHotelCheckoutTransformer,
  ],
  [RbcCheckoutRawEventName.ViewedPackageCheckout]: [
    useViewedPackageCheckoutTransformer,
  ],
  [RbcCheckoutRawEventName.ViewedActivitiesCheckout]: [
    useViewedActivitiesCheckoutTransformer,
  ],
  [PaymentRawEventName.ViewPayment]: [useViewPaymentTransformer],
  [PaymentRawEventName.ChoosePayment]: [useChoosePaymentTransformer],
  [TravelerRawEventName.ChooseTraveler]: [useChooseTravelerTransformer],
  [GridV2RawEventName.GridV2OptedOut]: [useGridV2OptedOutTransformer],
};

const INSURANCE_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  InsuranceRawEvent,
  InsuranceEvent
> = {
  [InsuranceRawEventName.ViewedInsurance]: [useViewedInsuranceTransformer],
  [InsuranceRawEventName.ViewedInsuranceTerms]: [
    useViewedInsuranceTermsTransformer,
  ],
  [InsuranceRawEventName.SelectedInsurance]: [useSelectedInsuranceTransformer],
};

const FLIGHT_PAYMENT_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  FlightPaymentRawEvent,
  FlightPaymentEvent
> = {
  [FlightPaymentRawEventName.SelectTerms]: [useSelectTermsTransformer],
  [FlightPaymentRawEventName.ViewedTerms]: [useViewedTermsTransformer],
};

const useEmitHTSEvent = createUseEmitRawEvent({
  transformerHooks: mergeEventTransformerHooks(
    CORE_COMMON_HTS_EVENT_TRANSFORMER_HOOKS,
    ALL_RBC_CARS_TRANSFORMERS,
    CARS_CHECKOUT_TRANSFORMER_HOOKS,
    ALL_RBC_FLIGHT_TRANSFORMERS,
    RBC_SPECIFIC_EVENT_TRANSFORMER_HOOKS,
    OFFER_EVENT_TRANSFORMER_HOOKS,
    UP_SELL_EVENT_TRANSFORMER_HOOKS,
    INSURANCE_EVENT_TRANSFORMER_HOOKS,
    FLIGHT_PAYMENT_EVENT_TRANSFORMER_HOOKS,
    ALL_RBC_HOTEL_TRANSFORMERS,
    HOTEL_CHECKOUT_HTS_EVENT_TRANSFORMER_HOOKS,
    CORE_FLIGHTS_PRICE_FREEZE_HTS_EVENT_TRANSFORMER_HOOKS,
    ALL_RBC_PACKAGE_TRANSFORMERS,
    AIR_PRICE_WATCH_HTS_EVENT_TRANSFORMER_HOOKS,
    ACTIVITIES_HTS_EVENT_TRANSFORMER_HOOKS,
    SELF_SERVE_HTS_EVENT_TRANSFORMER_HOOKS
  ),
  usePublishEvent,
});

/**
 * Registers the checkout machine tracking sink. Rendered as a child of
 * `AppTrackingContext.Provider` because `usePublishEvent` reads the UTM
 * trackers from that context.
 */
const CheckoutTrackingBridge = () => {
  useCheckoutTrackingSink(usePublishEvent());
  return null;
};

export const AppTrackingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const context = useAppTrackingProvider();
  const emitRawEventHooks = useMemo(() => [useEmitHTSEvent], []);

  return (
    <AppTrackingContext.Provider value={context}>
      <CheckoutTrackingBridge />
      <TrackingProvider emitRawEventHooks={emitRawEventHooks}>
        {children}
      </TrackingProvider>
    </AppTrackingContext.Provider>
  );
};
